<template>
  <div id="email_engagement_activity">
    <ContainerInfoItem
      source="email_engagement_activity_graph"
      title="Email engagement activity"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        id="email_engagement_activity"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="email_engagement_activity_graph"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import LineChart from "@/src/components/charts/LineChart.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { processLineChartData } from "@/src/utils/chartUtils.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const processEmailEngagementData = (data) => {
  return processLineChartData(data, {
    dateKey: "date",
    metrics: [
      { key: "email_sent", label: "Emails Sent" },
      { key: "email_opened", label: "Email Opens" },
      { key: "email_clicked", label: "Email Clicks" },
      { key: "email_replied", label: "Emails Replied" }
    ],
  });
};

const toFetchEmailCampaignEngagement = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchEmailCampaignEngagement(params);
    if (response?.success && response?.data?.data) {
      const { categories, seriesData } = processEmailEngagementData(
        response.data.data,
      );
      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching email campaign engagement: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value) toFetchEmailCampaignEngagement();
});

useAnalyticsRefresh(toFetchEmailCampaignEngagement, [range]);
</script>
